import { useState } from 'react';
import { Anchor, Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import type { Round } from '@flow/core';
import { run, useUseCases } from '../../app/services';

/**
 * The round's resolution, one line under the tabs; tap to read it all or edit it (e.g. a Parli topic announced as
 * the round starts). A Parli round without one gets a nudge.
 */
export const ResolutionBar = ({ round }: { round: Round }) => {
  const uc = useUseCases();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const resolution = round.resolution?.trim() ?? '';
  const edit = () => { setText(resolution); setOpen(true); };
  const parli = round.format.event === 'parli';
  if (!resolution && !parli) {
    return null; // Optional outside Parli; the round form has the field.
  }
  return (
    <>
      {resolution ? (
        <Group px="sm" py={3} gap={6} wrap="nowrap" style={{ borderBottom: '1px solid var(--grid-line)' }}>
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>Resolution</Text>
          <Anchor component="button" type="button" size="xs" c="var(--mantine-color-text)" lineClamp={1} onClick={edit} style={{ textAlign: 'left', minWidth: 0 }} data-testid="resolution">{resolution}</Anchor>
          <IconPencil size={12} style={{ flexShrink: 0, opacity: 0.5 }} />
        </Group>
      ) : (
        <Group px="sm" py={4} gap="xs" style={{ background: 'var(--mantine-color-yellow-light)' }}>
          <Text size="xs">No resolution yet. Parli topics change every round.</Text>
          <Button size="compact-xs" onClick={edit} data-testid="add-resolution">Add resolution</Button>
        </Group>
      )}
      <Modal opened={open} onClose={() => setOpen(false)} title="Resolution" size="lg">
        <form onSubmit={async (e) => { e.preventDefault(); if (await run(uc.setResolution(round.id, text))) setOpen(false); }}>
          <Stack>
            <Textarea autosize minRows={2} maxRows={8} value={text} onChange={(e) => setText(e.currentTarget.value)} placeholder="Resolved: …" data-autofocus aria-label="Resolution" />
            <Group justify="flex-end"><Button type="submit" variant="filled">Save</Button></Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
};
